import { Hourglass16, WarningFilled16 } from '@carbon/icons-react';
import { useBoolean } from 'ahooks';
import { Badge, Tooltip } from 'antd';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Project } from 'api/project';
import { Action, ObjectType } from 'api/pushes';
import { VerticalDivider } from 'components/atoms/divider/vertical-divider';
import { PlaceholderAvatarGroup } from 'components/molecules/placeholder-avatar';
import { toastService } from 'components/molecules/toast';
import { RbacButton } from 'components/organisms/rbac/components/rbac-buttons';
import { useInvalidateOldPlaceholderData } from 'hooks/placeholders/use-placeholder-invalidation';
import { ProjectResource } from 'hooks/rbac/useWithAccess';
import { ClickEventTags, setMarkerForDataDog } from 'hooks/use-data-dog-logger';
import { useTranslate } from 'hooks/use-translation';
import { useUserWsUpdates } from 'hooks/use-user-ws-updates';
import {
  useProjectPlaceholderUpdateStatus,
  useProjectPlaceholderUpdateStatusRefresh,
} from 'queries/projects/use-placeholder-update-progress';
import { PlaceholderResolutionModal } from './modal';
import { useUsersForProject } from './project-users';

const CustomRbacButton = styled(RbacButton)`
  width: 100%;
`;

export type StatusType = 'NONE' | 'IN_PROGRESS' | 'FAILED';

const PlaceHolderContainer = styled.div<{
  isInprogress: boolean;
  isFailed: boolean;
}>`
  display: flex;
  align-items: center;
  padding: var(--spacing-03);
  border-radius: var(--spacing-06);
  border: ${(props) =>
    props.isInprogress
      ? '1px solid var(--coolest-gray-30)'
      : props.isFailed
      ? '1px solid var(--red-60)'
      : 'none'};

  .ant-badge {
    display: flex;
    align-items: center;
  }
`;

const CustomWarning = styled(WarningFilled16)`
  color: var(--red-60);
`;

interface Props {
  project: Project;
}
export const PlaceholderResolution: FC<Props> = ({ project }) => {
  const [
    placeholderModalVisible,
    {
      setTrue: showPlaceholderResolutionModal,
      setFalse: hidePlaceholderResolutionModal,
    },
  ] = useBoolean(false);
  const { placeholders } = useUsersForProject({ project });
  const hasSomeUnresolvedPlaceholders = useMemo(
    () => placeholders.some((placeholder) => placeholder.resolved !== true),
    [placeholders],
  );

  const { data: teamsUpdateStatusInfo } = useProjectPlaceholderUpdateStatus(
    project.projectId,
  );

  const updateBulkStatus = useProjectPlaceholderUpdateStatusRefresh();
  const translate = useTranslate();
  const invalidateOldPlaceholderData = useInvalidateOldPlaceholderData(project);

  const onBulkUpdateSuccess = useCallback(() => {
    updateBulkStatus();
    toastService({
      toastContainer: {},
      title: translate('projects.placeholder_resolution.success.title'),
      message: translate(
        'projects.placeholder_resolution.success.bulk.message',
      ),
    }).success();
    hidePlaceholderResolutionModal();
    invalidateOldPlaceholderData();
  }, [
    updateBulkStatus,
    hidePlaceholderResolutionModal,
    translate,
    invalidateOldPlaceholderData,
  ]);

  useUserWsUpdates(() => {
    onBulkUpdateSuccess();
  }, [Action.Bulk, ObjectType.AsyncProcessor]);

  const checkTeamUpdationStatus = useCallback((): StatusType => {
    let status: StatusType = 'NONE';

    if (teamsUpdateStatusInfo && teamsUpdateStatusInfo.length) {
      if (
        teamsUpdateStatusInfo[0].status === 'IN_PROGRESS' ||
        teamsUpdateStatusInfo[0].status === 'SUBMITTED'
      ) {
        status = 'IN_PROGRESS';
      } else if (teamsUpdateStatusInfo[0].status === 'COMPLETED') {
        if (
          teamsUpdateStatusInfo[0]?.responseMeta?.response?.response?.teams
            ?.length
        ) {
          teamsUpdateStatusInfo[0].responseMeta.response.response.teams.forEach(
            (team) => {
              if (!team.status) {
                status = 'FAILED';
              }
            },
          );
        }
      }
    }

    return status;
  }, [teamsUpdateStatusInfo]);

  const BadgeContainer = () => (
    <Badge dot={hasSomeUnresolvedPlaceholders}>
      {Boolean(
        checkTeamUpdationStatus() === 'IN_PROGRESS' ||
          checkTeamUpdationStatus() === 'FAILED',
      ) && (
        <>
          {checkTeamUpdationStatus() === 'IN_PROGRESS' ? (
            <Hourglass16 />
          ) : (
            <CustomWarning />
          )}
          <VerticalDivider />
        </>
      )}

      <PlaceholderAvatarGroup
        size={16}
        isDisabled={Boolean(
          checkTeamUpdationStatus() === 'IN_PROGRESS' ||
            checkTeamUpdationStatus() === 'FAILED',
        )}
      />
    </Badge>
  );

  return (
    <>
      <Tooltip
        title={
          <FormattedMessage id="projects.preview.menu.manage_placeholder" />
        }
      >
        <CustomRbacButton
          data-cy="project.placeholder-resolution.trigger"
          icon={
            <PlaceHolderContainer
              isInprogress={checkTeamUpdationStatus() === 'IN_PROGRESS'}
              isFailed={checkTeamUpdationStatus() === 'FAILED'}
            >
              <BadgeContainer />
            </PlaceHolderContainer>
          }
          type="text"
          onClick={() => {
            setMarkerForDataDog(
              ClickEventTags.ProjectPlanPlaceholderResolutionPopup,
            );
            showPlaceholderResolutionModal();
          }}
          rule="TASK_ASSIGNEE:READ"
          resourceData={ProjectResource(project)}
        />
      </Tooltip>
      {placeholderModalVisible ? (
        <PlaceholderResolutionModal
          visible={placeholderModalVisible}
          onClose={hidePlaceholderResolutionModal}
          placeholders={placeholders}
          project={project}
          teamsUpdateStatusInfo={teamsUpdateStatusInfo}
          teamsUpdateStatus={checkTeamUpdationStatus()}
        />
      ) : null}
    </>
  );
};
